import { Link } from "react-router";
import { AlertCircle, CheckCircle2, Plus } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { formatCount } from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import { SegmentBadge } from "./SegmentBadge";
import { CustomerHealthBadge } from "./CustomerHealthBadge";
import { healthScoreFromFactorScores } from "./strategyTiers";
import { VisibilityProvenanceBadge } from "../account-allocation/VisibilityProvenanceBadge";

interface CustomerCardHeaderProps {
  customer: API.CustomerIntelligenceListItem;
  href?: string;
  onAddTicket?: (customerId: string) => void;
}

/**
 * Top row of a customer list card — name (linked when ``href`` is
 * given), segment badge, alert and ticket counters, and a "Ticket" CTA.
 * Reused by the pulse ``CustomerCard`` and the strategy
 * ``CustomerStrategyRowCard`` so both lenses share one header.
 */
export function CustomerCardHeader({
  customer,
  href,
  onAddTicket,
}: CustomerCardHeaderProps) {
  const alertCount = customer.open_alerts_count ?? 0;
  const ticketCount = customer.open_tickets_count ?? 0;
  const healthScore = healthScoreFromFactorScores(
    customer.strategy_summary?.account_health_factor_scores,
  );
  const isHealthOverridden =
    customer.strategy_summary?.health_tier_is_overridden ?? false;
  const healthOverrideTier = customer.strategy_summary?.health_tier;

  const handleAddTicket = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddTicket?.(customer.id);
  };

  return (
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {href ? (
            <Link
              to={href}
              onClick={(e) => e.stopPropagation()}
              className="font-semibold truncate text-primary hover:underline"
            >
              {customer.name}
            </Link>
          ) : (
            <h3 className="font-semibold truncate">{customer.name}</h3>
          )}
          {customer.customer_code && (
            <span className="text-xs text-muted-foreground shrink-0">
              {customer.customer_code}
            </span>
          )}
          <SegmentBadge segment={customer.segment_id ?? ""} />
          <CustomerHealthBadge
            score={healthScore}
            overrideTier={healthOverrideTier}
            isOverridden={isHealthOverridden}
          />
          <VisibilityProvenanceBadge
            visibilityReason={customer.visibility_reason}
            coveringForUserName={customer.covering_for_user_name}
            coverUntil={customer.cover_until}
          />
        </div>
      </div>
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 shrink-0">
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center gap-1.5 ${
              alertCount > 0 ? "text-destructive" : "text-muted-foreground/50"
            }`}
          >
            <AlertCircle className="size-4" />
            <span className="text-xs font-medium">
              {formatCount(alertCount)} Alert{alertCount !== 1 ? "s" : ""}
            </span>
          </div>
          <div
            className={`flex items-center gap-1.5 ${
              ticketCount > 0
                ? "text-muted-foreground"
                : "text-muted-foreground/50"
            }`}
          >
            <CheckCircle2 className="size-4" />
            <span className="text-xs font-medium">
              {formatCount(ticketCount)} Ticket{ticketCount !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        {onAddTicket && (
          <Button
            size="sm"
            onClick={handleAddTicket}
            className="flex items-center gap-1 md:ml-2 w-full md:w-auto justify-center"
          >
            <Plus className="size-4" />
            Ticket
          </Button>
        )}
      </div>
    </div>
  );
}
